import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@chakra-ui/react';
import { LuChevronDown, LuPlus } from 'react-icons/lu';

import { ComposeMenuItem, useComposeDropdownMenu } from './hooks';
import { SidebarRailTooltip } from '../../../SidebarRailTooltip/SidebarRailTooltip';

interface ComposeDropdownMenuProps {
  hasEmailAccounts: boolean;
  isExpanded: boolean;
  isLoading?: boolean;
  onCreateTask: () => void;
}

const stylesheet = {
  wrapper: 'relative flex items-center mb-4',
  wrapperFullWidth: 'relative flex items-center mb-4 w-full',
  triggerButton:
    'flex items-center justify-center w-7 h-11 rounded-r-semantic rounded-l-none shadow-md hover:shadow-lg dark:shadow-sm dark:hover:shadow transition-all duration-200 text-white border-t border-r border-b border-semantic-border dark:border-transparent p-0',
  menu: 'fixed z-[9999] bg-semantic-bg-surface rounded-semantic overflow-hidden shadow-lg border border-semantic-border min-w-[160px] py-1',
  menuItem:
    'flex items-center gap-2 w-full px-3 py-2 text-sm text-semantic-text-primary hover:bg-semantic-bg-hover transition-colors duration-150 cursor-pointer',
  menuItemIcon: 'text-semantic-text-secondary flex-shrink-0',
  taskButtonExpanded:
    'w-full ml-0 mb-4 rounded-semantic !py-2 !px-3 !h-11 shadow-md hover:shadow-lg dark:shadow-sm dark:hover:shadow transition-all duration-200 !font-medium',
  taskButtonCollapsed:
    '!w-11 !h-11 mb-4 !rounded-semantic !p-0 flex items-center justify-center shadow-md hover:shadow-lg dark:shadow-sm dark:hover:shadow transition-all duration-200',
  taskButtonIcon: 'mr-2',
};

function MenuItem({ item }: Readonly<{ item: ComposeMenuItem }>) {
  const { t } = useTranslation();
  const Icon = item.icon;

  return (
    <button
      type="button"
      className={stylesheet.menuItem}
      onClick={item.onClick}
      role="menuitem"
    >
      <Icon className={stylesheet.menuItemIcon} aria-hidden="true" />
      {t(item.labelKey)}
    </button>
  );
}

interface CreateDoBoxButtonProps {
  isExpanded: boolean;
  isLoading: boolean;
  onCreateTask: () => void;
}

function CreateDoBoxButton({
  isExpanded,
  isLoading,
  onCreateTask,
}: Readonly<CreateDoBoxButtonProps>) {
  const { t } = useTranslation();
  const label = t('components.composeButton.createDoBox');

  const button = (
    <Button
      variant="emerald"
      className={
        isExpanded
          ? stylesheet.taskButtonExpanded
          : stylesheet.taskButtonCollapsed
      }
      onClick={onCreateTask}
      isLoading={isLoading}
      aria-label={label}
    >
      <LuPlus
        className={isExpanded ? stylesheet.taskButtonIcon : undefined}
        aria-hidden="true"
      />
      {isExpanded && label}
    </Button>
  );

  if (isExpanded) {
    return button;
  }

  return <SidebarRailTooltip label={label}>{button}</SidebarRailTooltip>;
}

export function ComposeDropdownMenu({
  hasEmailAccounts,
  isExpanded,
  isLoading = false,
  onCreateTask,
}: Readonly<ComposeDropdownMenuProps>) {
  const { t } = useTranslation();
  const {
    menuRef,
    triggerRef,
    portalMenuRef,
    isMenuOpen,
    menuPosition,
    onToggleMenu,
    menuItems,
  } = useComposeDropdownMenu(onCreateTask);

  return (
    <div
      className={
        !hasEmailAccounts && isExpanded
          ? stylesheet.wrapperFullWidth
          : stylesheet.wrapper
      }
      ref={menuRef}
    >
      {hasEmailAccounts && isExpanded && (
        <>
          <Button
            ref={triggerRef}
            variant="emerald"
            className={stylesheet.triggerButton}
            onClick={onToggleMenu}
            aria-label={t('components.composeButton.openMenu')}
            aria-haspopup="true"
            aria-expanded={isMenuOpen}
            disabled={isLoading}
          >
            <LuChevronDown />
          </Button>
          {isMenuOpen &&
            menuPosition &&
            createPortal(
              <div
                ref={portalMenuRef}
                className={stylesheet.menu}
                role="menu"
                aria-label={t('components.composeButton.openMenu')}
                style={{ top: menuPosition.top, left: menuPosition.left }}
              >
                {menuItems.map((item) => (
                  <MenuItem key={item.id} item={item} />
                ))}
              </div>,
              document.body,
            )}
        </>
      )}

      {!hasEmailAccounts && (
        <CreateDoBoxButton
          isExpanded={isExpanded}
          isLoading={isLoading}
          onCreateTask={onCreateTask}
        />
      )}
    </div>
  );
}
